import React from 'react'
import PropTypes from 'prop-types'
import AccountDetailsForm from './AccountDetailsForm'
import TrustDetailsForm from '../TrustDetails/TrustDetailsForm'
import withOnboardingSection from '../../withOnboardingSection'
import {
  continueToNextSection,
  getPreviousSection
} from 'utils/financialAccountTypeNavigation'
import {
  allAccountDetails,
} from 'utils/financialAccountDetailsConfig'
import { updateFinancialAccount } from 'utils/apiRequest'
import style from './AccountDetails.module.scss'
import {
  SCHWAB_IRA,
  SCHWAB_ONE_TRUST,
  SCHWAB_CHARITABLE,
  SCHWAB_ONE_PERSONAL,
} from 'utils/financialAccountTypes.js'

const getFinancialAccount = (financialAccounts, financialAccountId) => (
  financialAccounts && financialAccounts.find(financialAccount =>
    financialAccount.id === financialAccountId)
)

const getJointOwner = (jointOwners, financialAccountId) => {
  const jointOwner = jointOwners && jointOwners.find(jointOwner =>
    jointOwner.financialAccountId === financialAccountId)
  return (jointOwner && jointOwner.data && jointOwner.data.attributes) || null
}

const prepareFormDataForRender = (
  prospect,
  financialAccount,
  jointOwner,
  accountConfig,
) => {
  if (!financialAccount) return

  const {
    attributes: {
      paperlessDelivery,
    }
  } = financialAccount

  const detailFields = {}

  const {
    accountHolderFormName,
    accountHolderFormLastName,
    jointAccountFormName,
    jointAccountFormLastName,
    paperlessFormName,
  } = accountConfig.fields

  if (prospect) {
    detailFields[`${accountHolderFormName}`] = prospect.firstName
    detailFields[`${accountHolderFormLastName}`] = prospect.lastName
  }
  if (jointAccountFormName && jointOwner) {
    detailFields[`${jointAccountFormName}`] = jointOwner.firstName
    detailFields[`${jointAccountFormLastName}`] = jointOwner.lastName
  }
  if (financialAccount) {
    if (paperlessDelivery != null && paperlessDelivery !== '') {
      detailFields[`${paperlessFormName}`] = paperlessDelivery ? 'Yes' : 'No'

      Object.keys(accountConfig.fields).map(key =>
        detailFields[key] = financialAccount.attributes[key]
      )
    }
  }

  return detailFields
}

const getPreviousSectionInfo = (prevPageInfo) => {
  const {
    financialAccounts,
    financialAccountId,
    financialAccountType,
    jointOwner,
    isMobile,
  } = prevPageInfo
  return getPreviousSection({
    currentFinancialAccountId: financialAccountId,
    currentFinancialAccountType: financialAccountType,
    currentSection: 'accountDetails',
    financialAccounts,
    jointOwner,
    isMobile,
  })
}

const AccountDetails = (props) => {
  const {
    hide,
    isLoading,
    isMobile,
    prospect,
    financialAccounts,
    jointOwners,
    editFinancialAccount,
    location: {
      payload: {
        financialAccountId,
        financialAccountType,
      }
    },
    openModal,
  } = props
  const accountConfig = allAccountDetails[`${financialAccountType}`]

  const { fieldsToSubmit } = accountConfig

  let { 
    description,
    upperTitle, 
  } = accountConfig

  const isTrustDetails = (accountConfig.accountType === SCHWAB_ONE_TRUST.ACCOUNT_TYPE_ROUTE)
  const financialAccount = getFinancialAccount(financialAccounts, financialAccountId)
  const jointOwner = getJointOwner(jointOwners, financialAccountId)
  const data = prepareFormDataForRender(
    prospect,
    financialAccount,
    jointOwner,
    accountConfig
  )
  const prevPageInfo = getPreviousSectionInfo({
    financialAccounts,
    financialAccountId,
    financialAccountType,
    jointOwner,
    isMobile,
  })

  const submitAction = (formData) => {
    const submitData = {
      ...formData,
      reviewedDetails: true,
    }

    delete submitData.deceasedSsnLastFour
    delete submitData.minorSsnLastFour
    delete submitData.donorSsnLastFour

    return updateFinancialAccount(financialAccountId, submitData)
  }

  const { CUSTODIAL_IRA, CUSTODIAL_ROTH_IRA } = SCHWAB_IRA.ACCOUNT_TYPES
  const isIRA = financialAccountType === SCHWAB_IRA.ACCOUNT_TYPE_ROUTE
  const isCharitable = financialAccountType === SCHWAB_CHARITABLE.ACCOUNT_TYPE_ROUTE
  const isPersonal = financialAccountType === SCHWAB_ONE_PERSONAL.ACCOUNT_TYPE_ROUTE

  let subType
  let irrevocableTrustType

  if ((isTrustDetails || isIRA || isPersonal || isCharitable)  && financialAccounts.length > 0) {
    const currentFinancialAccount = getFinancialAccount(financialAccounts, financialAccountId)

    if (currentFinancialAccount) {
      ({ subType, irrevocableTrustType } = currentFinancialAccount.attributes)
    }

    if (subType) {
      accountConfig.fields.accountTypeValue = subType
    }
  }

  const isIndividual401k = subType  === SCHWAB_IRA.ACCOUNT_TYPES.INDIVIDUAL_401K
  const isCustodial = [CUSTODIAL_IRA, CUSTODIAL_ROTH_IRA].includes(subType)
  const isUTMA = subType === SCHWAB_ONE_PERSONAL.ACCOUNT_TYPES.UTMA
  const FormToRender = isTrustDetails ? TrustDetailsForm : AccountDetailsForm

  const Section = withOnboardingSection(FormToRender)

  if (isCharitable) {
    description = `Let's look at your Donor Advised Fund.`
  } else if (isIndividual401k) {
    description = `Let's look at your Individual 401(k).`
    upperTitle = `401(k) Account`
  }

  const handleContinueToNextSection = () => {
    continueToNextSection({
      financialAccounts,
      currentFinancialAccountId: financialAccountId,
      currentFinancialAccountType: financialAccountType,
      currentSection: 'accountDetails',
      jointOwner,
      isUTMA,
      isCustodial,
    })
  }

  return (
    <Section
      upperTitle={upperTitle}
      isMobile={isMobile}
      formName="Account Details"
      description={[description]}
      submitAction={submitAction}
      prevPageInfo={prevPageInfo}
      isLoading={isLoading}
      hide={hide || isLoading}
      pageContainerClassName={style.AccountDetails}
      data={data}
      accountSubType={subType}
      irrevocableTrustType={irrevocableTrustType}
      accountConfig={accountConfig}
      openModal={openModal}
      setData={editFinancialAccount}
      goNextSection={handleContinueToNextSection}
      mapValues={fieldsToSubmit} />
  )
}

AccountDetails.propTypes = {
  hide: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool.isRequired,
  location: PropTypes.object.isRequired,
  prospect: PropTypes.object.isRequired,
  jointOwners: PropTypes.arrayOf(PropTypes.object).isRequired,
  financialAccounts: PropTypes.arrayOf(PropTypes.object).isRequired,
  editFinancialAccount: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
}

export default AccountDetails



// WEBPACK FOOTER //
// ./src/components/Onboarding/Accounts/AccountDetails/AccountDetails.jsx